import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import qs from 'qs';
import { Loader, TabsPanel } from 'ui';
import { useQuery, useFeatures } from 'shared/hooks';
import { useFetchPortfolio } from '../../GraphQL/portfolios/hooks';
import { PortfolioEntities } from './PortfolioEntities';
import { PortfolioEvents } from './PortfolioEvents';
import { PortfolioInstruments } from './PortfolioInstruments';
import { PortfolioRatingNews } from './PortfolioRatingNews';
import { PortfolioResearch } from './PortfolioResearch';
import { PortfolioSummary } from './PortfolioSummaryTab/PortfolioSummary';
import { PortfolioUploads } from './PortfolioUploads';
import { PortfolioType, Portfolio as PortfolioModel } from '../types';
import { PortfolioFunds } from './PortfolioFunds';

type PortfolioParams = {
  portfolioId: string;
};

export const Portfolio: FC = () => {
  const { portfolioId = '' } = useParams<PortfolioParams>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = useQuery() as { activeTab: string };
  const { data, loading, refetch: refetchPortfolio } = useFetchPortfolio(portfolioId);
  const [entitiesCounter, setEntitiesCounter] = useState(0);
  const [instrumentsCounter, setInstrumentsCounter] = useState(0);

  const refetch = () => {
    refetchPortfolio();
  };

  const {
    eventsInPortfolioManager,
    uploadsHistoryInPortfolioManager,
    fundAnalysisPortfolioManager,
  } = useFeatures();

  const portfolioData = data?.portfolio ?? ({ noOfItems: {} } as PortfolioModel);

  const {
    name,
    noOfItems: { entities, instruments, funds, ratingNews, researches, events, uploads },
    portfolioType,
    summary,
  } = portfolioData;

  const isReference =
    !!portfolioType &&
    [PortfolioType.REFERENCE_PORTFOLIO_EUROPE, PortfolioType.REFERENCE_PORTFOLIO_GLOBAL].includes(
      portfolioType,
    );

  const isRequestHistory = PortfolioType.REQUEST_HISTORY === portfolioType;

  const defaultActiveTab = isRequestHistory ? 'entities' : 'summary';

  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab);

  const { activeTab: savedActiveTab } = queryParams;

  useEffect(() => {
    if (loading) {
      return;
    }

    if (savedActiveTab && savedActiveTab !== activeTab) {
      setActiveTab(savedActiveTab);
    } else if (!savedActiveTab) {
      const searchQuery = isRequestHistory
        ? { ...queryParams, activeTab: defaultActiveTab, ratingType: 'esg-impact' }
        : { ...queryParams, activeTab: defaultActiveTab };
      navigate(
        {
          pathname: location.pathname,
          search: qs.stringify(searchQuery, { addQueryPrefix: true }),
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedActiveTab, loading]);

  const onTabChange = useCallback(
    (_: never, value: string) => {
      if (activeTab !== value || activeTab !== savedActiveTab) {
        navigate(
          {
            pathname: location.pathname,
            search: qs.stringify({ activeTab: value }, { addQueryPrefix: true }),
          },
          { replace: true },
        );
      }
    },
    [activeTab, savedActiveTab],
  );

  if (loading) {
    return <Loader />;
  }

  const handleEntitiesFilteredCounter = (entCount) => {
    return setEntitiesCounter(entCount);
  };

  const handleInstrumentsFilteredCounter = (instCount) => {
    return setInstrumentsCounter(instCount);
  };

  return (
    <>
      {/* @ts-ignore */}
      <TabsPanel
        key={name}
        active={isRequestHistory && activeTab === 'summary' ? defaultActiveTab : activeTab}
        hasShadow="true"
        onChange={onTabChange}
      >
        {!isRequestHistory && (
          <div label={t('PORTFOLIO.PORTFOLIO_SUMMARY')} value="summary" key="summary">
            <PortfolioSummary
              summary={summary}
              noOfItems={portfolioData.noOfItems}
              portfolioId={portfolioId}
            />
          </div>
        )}
        <div
          label={t('ENTITIES')}
          value="entities"
          key="entities"
          counter={entitiesCounter || entities}
        >
          <PortfolioEntities
            portfolioId={portfolioId}
            refetchPortfolio={refetch}
            portfolioData={portfolioData}
            totalCounter={handleEntitiesFilteredCounter}
          />
        </div>
        {!isRequestHistory && (
          <div
            label={t('INSTRUMENTS')}
            value="instruments"
            key="instruments"
            counter={instrumentsCounter || instruments}
          >
            <PortfolioInstruments
              portfolioId={portfolioId}
              refetchPortfolio={refetch}
              isReferencePortfolio={isReference}
              portfolioData={portfolioData}
              totalCounter={handleInstrumentsFilteredCounter}
            />
          </div>
        )}
        {fundAnalysisPortfolioManager && !isReference && !isRequestHistory && (
          <div label={t('FUNDS')} value="funds" key="funds" counter={funds}>
            <PortfolioFunds
              portfolioId={portfolioId}
              total={funds}
              refetchPortfolio={refetch}
              isReferencePortfolio={isReference}
              portfolioData={portfolioData}
            />
          </div>
        )}
        {!isReference && !isRequestHistory && (
          <div label={t('RATING_NEWS')} value="ratingNews" key="ratingNews" counter={ratingNews}>
            <PortfolioRatingNews
              portfolioId={portfolioId}
              total={ratingNews}
              refetchPortfolio={refetch}
              portfolioData={portfolioData}
            />
          </div>
        )}
        {!isReference && !isRequestHistory && (
          <div label={t('RESEARCH')} value="research" key="research" counter={researches}>
            <PortfolioResearch
              portfolioId={portfolioId}
              total={researches}
              refetchPortfolio={refetch}
              portfolioData={portfolioData}
            />
          </div>
        )}
        {eventsInPortfolioManager && !isReference && !isRequestHistory && (
          <div label={t('EVENTS')} value="events" key="events" counter={events}>
            <PortfolioEvents
              portfolioId={portfolioId}
              total={events}
              refetchPortfolio={refetch}
              portfolioData={portfolioData}
            />
          </div>
        )}
        {uploadsHistoryInPortfolioManager && !isRequestHistory && (
          <div label={t('UPLOADS')} value="uploads" key="uploads" counter={uploads}>
            <PortfolioUploads portfolioId={portfolioId} portfolioData={portfolioData} />
          </div>
        )}
      </TabsPanel>
    </>
  );
};
